// SGNL-179 — Reports tab.
//
// Standalone tab for leadership to pick a report type, narrow scope, and
// export a branded PDF for distribution. Today it surfaces a single
// report ("Pipeline Progress") that reuses the existing Progress
// rendering (renderWeeklyCallTab) plus its built-in scope picker + PDF
// export.
//
// The value-add over the Progress tab itself:
//   - A "Report type" selector at the top of the page. Today there's
//     only one option, but the surface is wired for adding more
//     (Pipeline Forecast, Customer Health) without redesigning the nav.
//   - A short caption framing the report for the reader. The Progress
//     tab is a working surface; the Reports tab is a distribution
//     surface. Same data, different intent.
//   - When future reports land, the Progress tab keeps being the
//     interactive analysis surface; Reports becomes the "I want a PDF
//     to send to my CXO" entry point.
//
// Implementation note: this view is a thin wrapper around
// renderWeeklyCallTab. We do NOT duplicate the Progress rendering —
// duplicate code would split the bucket logic, the date picker, the
// AI Summary, and the export flow. Reports inherits all of them.

import { esc } from "../lib/format";
import { applyFilters, getState } from "../state";
import type { OpportunityRow } from "../types";
import {
  _resetPipelinePacingReportState,
  renderPipelinePacingReport,
} from "./pipeline_pacing_report";
import { renderWeeklyCallTab } from "./weekly_call";

type ReportId = "pipeline-progress" | "pipeline-pacing";

/**
 * Renderer dispatch table — each report id maps to (a) a `<body>`
 * delegate that paints the report content, and (b) the metadata the
 * picker card surfaces. Table-driven so adding a third report is a
 * one-entry change here, not an if/else chain edit (ARCH-02 pre-PR).
 * Note the delegate ignores `filteredOpps` for reports that don't use
 * the global filter bar — kept on the type for the Progress signature.
 */
type ReportDelegate = (
  body: HTMLElement,
  filteredOpps: OpportunityRow[] | null,
) => Promise<void>;

interface ReportRegistryEntry {
  label: string;
  description: string;
  render: ReportDelegate;
}

const REPORT_REGISTRY: Record<ReportId, ReportRegistryEntry> = {
  "pipeline-progress": {
    label: "Pipeline Progress",
    description:
      "Week-over-week deal movement: closed lost, drop in revenue, increase in revenue, new opportunities. Same data as the Progress tab, exported as a distributable PDF.",
    render: (body, filtered) => renderWeeklyCallTab(body, filtered),
  },
  "pipeline-pacing": {
    label: "Pipeline Pacing Dashboard",
    description:
      "Weekly Generation / Qualification / Conversion pacing against CY budget. Replicates the NMC review sheet — ITB / Qualified / Unqualified totals, Revenue Visibility, Go-Get, Coverage, plus per-phase weekly targets and a 4-week scorecard.",
    render: (body) => renderPipelinePacingReport(body),
  },
};

const REPORT_TYPES: Array<{ id: ReportId; label: string; description: string }> = (
  Object.entries(REPORT_REGISTRY) as Array<[ReportId, ReportRegistryEntry]>
).map(([id, entry]) => ({ id, label: entry.label, description: entry.description }));

// Track the active report so re-renders driven by global-filter changes
// keep the selection. Module-scope persists across re-mounts of the
// same tab; test seam exposed below.
let _activeReport: ReportId = "pipeline-progress";

// Q-03 (PR #113 code review): single-flight guard for concurrent
// renderReportsTab calls. The state subscriber fires synchronously on
// every filter-bar change, and renderReportsTab awaits the delegated
// renderWeeklyCallTab. Without this token, a second call's host.innerHTML
// = ... wipes out the first call's body reference; the first awaited
// call then writes its delegated render into a detached node. Each call
// increments the token; an in-flight call short-circuits if the token
// has moved past its captured value.
let _renderToken = 0;

/** Test seam: reset module state so test cases don't share selection.
 *
 * Cascades into every delegate's own module state so a vitest suite
 * that exercises one report doesn't leak counter / cached-result
 * state into a sibling suite (ARCH-01 pre-PR). The Progress delegate
 * has no module state of its own; only the pacing delegate currently
 * needs the cascade. Add new reports to the list as they ship. */
export function _resetReportsModuleState(): void {
  _activeReport = "pipeline-progress";
  _renderToken = 0;
  _resetPipelinePacingReportState();
}

function reportPicker(active: ReportId): string {
  // Each report renders as a clickable card. Future-proof: when there's
  // > 1 option, the cards grid; with 1 option, it visually anchors the
  // page so the user understands what they're looking at.
  const cards = REPORT_TYPES.map((r) => {
    const isActive = r.id === active;
    return `<button
      type="button"
      class="report-type-card ${isActive ? "is-active" : ""}"
      data-action="select-report"
      data-report-id="${esc(r.id)}"
      data-testid="report-type-${esc(r.id)}"
      aria-pressed="${isActive}"
    >
      <span class="report-type-label">${esc(r.label)}</span>
      <span class="report-type-description">${esc(r.description)}</span>
    </button>`;
  }).join("");
  return `
    <section class="reports-picker" data-testid="reports-picker">
      <h2 class="reports-h2">Reports</h2>
      <p class="reports-lead muted">
        Pick a report type, narrow the scope from the picker below, then export a branded PDF for distribution.
        BU and owner filters from the global filter bar apply automatically.
      </p>
      <div class="report-type-list">${cards}</div>
    </section>`;
}

/** Render the Reports tab. The body underneath the report-type picker is
 * the active report's content; for v0 that's the Progress rendering. */
export async function renderReportsTab(
  host: HTMLElement,
  filteredOpps: OpportunityRow[] | null = null,
): Promise<void> {
  // Q-03 (PR #113): single-flight guard against concurrent re-renders.
  const token = ++_renderToken;
  host.innerHTML = `
    <div class="tab-content reports-tab" data-testid="reports-tab">
      ${reportPicker(_activeReport)}
      <section
        class="reports-body"
        data-testid="reports-body"
        data-active-report="${esc(_activeReport)}"
      ></section>
    </div>`;
  const body = host.querySelector<HTMLElement>('[data-testid="reports-body"]');
  if (body) {
    // Table-driven dispatch — pulls the renderer for the active
    // report id straight from REPORT_REGISTRY. Adding a new report is
    // a single registry-entry change; no if/else maintenance.
    await REPORT_REGISTRY[_activeReport].render(body, filteredOpps);
    // If another render started while we were awaiting, the body we
    // captured above is now detached. Don't re-attach our event
    // listeners — the newer render owns the live DOM.
    if (token !== _renderToken) return;
  }

  // Selecting a different report type re-renders the body. With only one
  // option today this is a no-op, but the wiring stays so adding the
  // next report is a 3-line change to REPORT_TYPES + a dispatcher branch.
  //
  // ARCH-01 / Q-02 (PR #113): read filteredOpps fresh from state at
  // click time. Closure-capturing the param from the outer render call
  // would serve stale data when the global filter bar has changed since
  // the initial render. Mirrors the Apply-button fresh-state pattern in
  // weekly_call.ts.
  host.querySelectorAll<HTMLButtonElement>('[data-action="select-report"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.reportId as ReportId | undefined;
      if (!id || id === _activeReport) return;
      _activeReport = id;
      const state = getState();
      const fresh = state.data ? applyFilters(state.data.opps, state.filters) : null;
      void renderReportsTab(host, fresh);
    });
  });
}
